import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import VenueDrawer from "@/pages/admin/VenueDrawer";
import LeadsImportModal from "@/pages/admin/LeadsImportModal";
import {
  Users, Sparkles, CalendarPlus, Layers, TrendingUp, Skull,
  Search, ArrowLeft, RefreshCw, Mail, Phone, Ticket, Ban, Upload, Trash2, Newspaper,
} from "lucide-react";

const COLUMNS = [
  { key: "new_lead",             label: "New Lead",              icon: Users,        tone: "zinc",    hint: "Signed up · no login yet" },
  { key: "new_account_created",  label: "New Account Created",   icon: Sparkles,     tone: "sky",     hint: "Logged in · no events yet" },
  { key: "event_created",        label: "Event Created",         icon: CalendarPlus, tone: "indigo",  hint: "Has 1 event" },
  { key: "multiple_events",      label: "Multiple Events",       icon: Layers,       tone: "violet",  hint: "Has 2+ events, no sales" },
  { key: "actively_processing",  label: "Actively Processing",   icon: TrendingUp,   tone: "emerald", hint: "Has sold ≥ 1 ticket" },
  { key: "dead",                 label: "Dead User",             icon: Skull,        tone: "red",     hint: "No activity in 30+ days" },
];

const toneStyles = {
  zinc:    { chip: "bg-zinc-100 text-zinc-700",       dot: "bg-zinc-400",    ring: "ring-zinc-200",    accent: "text-zinc-600"    },
  sky:     { chip: "bg-sky-50 text-sky-700",          dot: "bg-sky-500",     ring: "ring-sky-200",     accent: "text-sky-600"     },
  indigo:  { chip: "bg-indigo-50 text-indigo-700",    dot: "bg-indigo-500",  ring: "ring-indigo-200",  accent: "text-indigo-600"  },
  violet:  { chip: "bg-violet-50 text-violet-700",    dot: "bg-violet-500",  ring: "ring-violet-200",  accent: "text-violet-600"  },
  emerald: { chip: "bg-emerald-50 text-emerald-700",  dot: "bg-emerald-500", ring: "ring-emerald-200", accent: "text-emerald-600" },
  red:     { chip: "bg-red-50 text-red-700",          dot: "bg-red-500",     ring: "ring-red-200",     accent: "text-red-600"     },
};

import { formatMoney } from "@/lib/api";

const money = (p, country) => formatMoney(p, country === "US" ? "USD" : "GBP");
const timeAgo = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const day = 86400000;
  const hr = 3600000;
  const mn = 60000;
  if (diff < mn) return "just now";
  if (diff < hr) return `${Math.floor(diff / mn)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

function VenueCard({ venue, tone, onClick, onDelete }) {
  const styles = toneStyles[tone] || toneStyles.zinc;
  const isLead = !!venue.is_imported_lead;
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        data-testid={`pipeline-card-${venue.user_id}`}
        className={`w-full text-left bg-white rounded-xl border ${isLead ? "border-dashed border-zinc-300" : "border-zinc-200"} p-4 hover:border-zinc-900 hover:shadow-sm transition ring-1 ${styles.ring}/50`}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-full bg-zinc-900 text-white grid place-items-center font-display font-black text-sm">
            {(venue.venue_name || venue.name || venue.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-sm truncate">{venue.venue_name || venue.name || "—"}</div>
              {isLead && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[9px] font-mono uppercase tracking-widest">
                  Imported
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-500 truncate">{venue.email}</div>
            {venue.contact_phone && (
              <div className="text-[11px] text-zinc-400 truncate mt-0.5">{venue.contact_phone}</div>
            )}
          </div>
        </div>

        {!isLead && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
            <div className="rounded-md bg-zinc-50 px-2 py-1.5">
              <div className="text-zinc-400 font-mono uppercase tracking-wider">Events</div>
              <div className="mt-0.5 font-display font-bold text-sm text-zinc-900">{venue.events_count}</div>
            </div>
            <div className="rounded-md bg-zinc-50 px-2 py-1.5">
              <div className="text-zinc-400 font-mono uppercase tracking-wider">Sold</div>
              <div className="mt-0.5 font-display font-bold text-sm text-zinc-900">{venue.tickets_sold}</div>
            </div>
            <div className="rounded-md bg-zinc-50 px-2 py-1.5">
              <div className="text-zinc-400 font-mono uppercase tracking-wider">GMV</div>
              <div className="mt-0.5 font-display font-bold text-sm text-zinc-900">{money(venue.revenue_pence, venue.country)}</div>
            </div>
          </div>
        )}

        {isLead && venue.notes && (
          <div className="mt-3 rounded-md bg-zinc-50 px-2 py-1.5 text-[11px] text-zinc-600 line-clamp-2">
            {venue.notes}
          </div>
        )}

        <div className="mt-3 flex items-center justify-between text-[11px]">
          <span className={`inline-flex items-center gap-1 ${styles.accent} font-medium`}>
            <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`} />
            {isLead ? (venue.source || "Lead") : (venue.stripe_onboarded ? "Stripe on" : "Stripe off")}
          </span>
          <span className="text-zinc-400">{timeAgo(venue.last_activity_at)}</span>
        </div>
      </button>
      {isLead && onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(venue.lead_id); }}
          data-testid={`lead-delete-${venue.lead_id}`}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50"
          aria-label="Delete lead"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Column({ col, venues, filter, onSelect, onDeleteLead, onImport }) {
  const styles = toneStyles[col.tone] || toneStyles.zinc;
  const Icon = col.icon;
  const filtered = useMemo(() => {
    const q = (filter || "").toLowerCase().trim();
    if (!q) return venues;
    return venues.filter((v) => (
      (v.email || "").toLowerCase().includes(q) ||
      (v.name || "").toLowerCase().includes(q) ||
      (v.venue_name || "").toLowerCase().includes(q) ||
      (v.contact_phone || "").toLowerCase().includes(q)
    ));
  }, [venues, filter]);

  const isNewLead = col.key === "new_lead";

  return (
    <div className="shrink-0 w-[320px] flex flex-col bg-zinc-50 rounded-2xl border border-zinc-200" data-testid={`pipeline-column-${col.key}`}>
      <div className="p-4 border-b border-zinc-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-8 h-8 rounded-lg grid place-items-center ${styles.chip}`}>
              <Icon className="w-4 h-4" />
            </span>
            <div>
              <div className="font-display font-bold text-sm">{col.label}</div>
              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">{col.hint}</div>
            </div>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium ${styles.chip}`} data-testid={`pipeline-count-${col.key}`}>
            {filtered.length}
          </span>
        </div>
        {isNewLead && (
          <button
            type="button"
            onClick={onImport}
            data-testid="pipeline-import-csv"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md bg-white border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 text-xs font-medium text-zinc-700 transition"
          >
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </button>
        )}
      </div>
      <div className="p-3 space-y-3 overflow-y-auto flex-1" style={{ maxHeight: "calc(100vh - 260px)" }}>
        {filtered.length === 0 ? (
          <div className="text-xs text-zinc-400 italic text-center py-10">
            {isNewLead ? "Upload a CSV to add prospects here." : "No accounts here yet."}
          </div>
        ) : (
          filtered.map((v) => (
            <VenueCard
              key={v.user_id}
              venue={v}
              tone={col.tone}
              onClick={() => onSelect(v)}
              onDelete={v.is_imported_lead ? onDeleteLead : undefined}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await http.get("/admin/pipeline");
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Remove this lead from the pipeline?")) return;
    try {
      await http.delete(`/admin/leads/${leadId}`);
      toast.success("Lead removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete lead");
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50" data-testid="admin-forbidden">
        <div className="max-w-md text-center p-8">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 text-red-600 grid place-items-center mb-4">
            <Ban className="w-6 h-6" />
          </div>
          <h1 className="font-display font-black text-2xl">Admin only</h1>
          <p className="mt-2 text-zinc-500 text-sm">This area is restricted to internal Affra admins.</p>
          <Link to="/dashboard"><Button className="mt-6 rounded-full">Back to your dashboard</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center">
              <Logo size="h-10" />
            </Link>
            <div className="h-6 w-px bg-zinc-200" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">/ admin</div>
              <div className="font-display font-bold text-lg leading-none mt-1">Pipeline</div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-1 justify-end min-w-0">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
              <Input
                data-testid="admin-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search email, venue, phone…"
                className="pl-9 h-10"
              />
            </div>
            <Button variant="outline" onClick={load} data-testid="admin-refresh" className="rounded-full">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Link to="/admin/press">
              <Button variant="outline" className="rounded-full text-sm" data-testid="admin-nav-press">
                <Newspaper className="w-4 h-4 mr-2" /> Press
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </Link>
          </div>
        </div>

        {/* Totals row */}
        {data && (
          <div className="max-w-[1600px] mx-auto px-6 pb-3 flex items-center gap-6 text-xs flex-wrap" data-testid="admin-totals">
            <span className="text-zinc-500">
              <strong className="text-zinc-900">{data.totals?.all || 0}</strong> accounts total
            </span>
            {COLUMNS.map((c) => (
              <span key={c.key} className="text-zinc-500">
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${toneStyles[c.tone].dot}`} />
                <strong className="text-zinc-900">{data.totals?.[c.key] || 0}</strong> {c.label.toLowerCase()}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Board */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="admin-error">
            {error}
          </div>
        )}
        {loading && !data ? (
          <div className="grid place-items-center py-24 text-sm text-zinc-500">Loading pipeline…</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="pipeline-board">
            {COLUMNS.map((col) => (
              <Column
                key={col.key}
                col={col}
                venues={data?.columns?.[col.key] || []}
                filter={filter}
                onSelect={(v) => {
                  // Imported leads have no full detail endpoint — surface a lightweight drawer instead
                  if (v.is_imported_lead) {
                    setSelected({ ...v, _leadOnly: true });
                  } else {
                    setSelected(v);
                  }
                }}
                onDeleteLead={deleteLead}
                onImport={() => setImportOpen(true)}
              />
            ))}
          </div>
        )}
      </main>

      {selected && !selected._leadOnly && (
        <VenueDrawer
          userId={selected.user_id}
          onClose={() => setSelected(null)}
          onRefresh={load}
        />
      )}

      {selected && selected._leadOnly && (
        <LeadQuickDrawer
          lead={selected}
          onClose={() => setSelected(null)}
          onDelete={() => { deleteLead(selected.lead_id); setSelected(null); }}
        />
      )}

      {importOpen && (
        <LeadsImportModal
          onClose={() => setImportOpen(false)}
          onImported={load}
        />
      )}
    </div>
  );
}

/** Lightweight drawer for imported leads (they don't have full activity yet). */
function LeadQuickDrawer({ lead, onClose, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-quick-drawer">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-zinc-900/40 backdrop-blur-sm"
      />
      <aside className="w-full max-w-md bg-white shadow-2xl overflow-y-auto">
        <div className="p-6 border-b border-zinc-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">/ imported lead</div>
            <div className="mt-1 font-display font-black text-2xl tracking-tight truncate">
              {lead.venue_name || lead.name || lead.email}
            </div>
            <div className="text-sm text-zinc-500 truncate">{lead.email}</div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-900" aria-label="Close">✕</button>
        </div>
        <div className="p-6 space-y-3 text-sm">
          <InfoField label="Email"   value={lead.email} />
          <InfoField label="Phone"   value={lead.contact_phone} />
          <InfoField label="Venue"   value={lead.venue_name} />
          <InfoField label="Source"  value={lead.source} />
          <InfoField label="Notes"   value={lead.notes} />
          <InfoField label="Added"   value={lead.created_at ? new Date(lead.created_at).toLocaleString("en-GB") : "—"} />
          <div className="pt-4 border-t border-zinc-100 flex justify-end">
            <button
              type="button"
              onClick={onDelete}
              data-testid="lead-quick-delete"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm bg-red-50 text-red-700 hover:bg-red-100"
            >
              Remove lead
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-zinc-100 last:border-none">
      <div className="w-24 shrink-0 text-[10px] font-mono uppercase tracking-widest text-zinc-500 pt-1">{label}</div>
      <div className="text-sm text-zinc-900 break-words min-w-0">{value || "—"}</div>
    </div>
  );
}

// Re-exported for potential unit tests
export { COLUMNS, timeAgo, money };
